import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { complexTableQuery } from "../../common/utils/tableUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = e => ensureInteger(e.id);

const equipmentSuperSlice = createSlice({
    name: 'superEquipment',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, e) => { obj[key(e)] = e; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(e => state.data[key(e)] = e);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(e => delete state.data[key(e)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => equipmentSuperSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'equipment_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'equipment_one');

const fetchAllEquipment = network => network.get('/api/v2/superEquipment', equipmentSuperSlice.actions.set, visualizeFailure('get'));

export const createEquipment = (network, equipment) => network.post('/api/v1/equipment', equipment, equipmentSuperSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateEquipment = (network, delta) => network.put('/api/v1/equipment', delta, equipmentSuperSlice.actions.addOrUpdate, visualizeFailure('update'));
export const deleteEquipment = (network, id) => network.delete('/api/v1/equipment/' + window.encodeURIComponent(id), equipmentSuperSlice.actions.remove, visualizeFailure('delete'));
// #endregion

// #region selectors
const selectEquipmentData = state => state.superEquipment.data;
const selectAllEquipment = createSelector([selectEquipmentData], equipment => Object.values(equipment))
const selectSingleEquipment = id => createSelector([selectEquipmentData], equipment => equipment[id]);
const selectEquipment = ids => createSelector([selectEquipmentData], equipment => ensureArray(ids).reduce((result, id) => { if (equipment[id]) { result.push(equipment[id]) } return result }, []));
const selectSearchEquipment = search => createSelector([selectEquipmentData], equipment => {
    let ignoreFields = Object.keys(search).filter(f => search[f] === undefined || search[f] === null || (Array.isArray(search[f]) && search[f].length === 0));
    let results = Object.values(equipment);
    return complexTableQuery(results, search, { ignoreFields });
});
// #endregion

const listen = {
    equipmentCreated: (equipment, { dispatch, websocket }) => {
        markFetched(websocket, 'equipment', key(equipment))
        dispatch(equipmentSuperSlice.actions.addOrUpdate(equipment))
    },
    equipmentUpdated: (equipment, { dispatch }) => dispatch(equipmentSuperSlice.actions.addOrUpdate(equipment)),
    equipmentRemoved: (equipment, { dispatch }) => dispatch(equipmentSuperSlice.actions.remove(equipment)),
}

const init = { run: fetchAllEquipment, listen, immortal: true };

export const [useSuperAllEquipment, realizeSuperAllEquipment] = dataHookFactory(selectAllEquipment, init);
export const [useSuperEquipment, realizeSuperEquipment] = dataHookFactory(selectEquipment, ids => init);
export const [useSuperSingleEquipment, realizeSuperSingleEquipment] = dataHookFactory(selectSingleEquipment, id => init);
export const [useEquipmentSearch] = dataHookFactory(selectSearchEquipment, search => init);

const fullRoles = ['administrator', 'equipment manager' ];
const readonlyRoles = ['auditor', 'equipment auditor'];

const checkPermissions = (credentials, equipment, requiredRoles, condition = true) => {
    return (
        credentials.hasRole(requiredRoles, { office: equipment?.office }) &&
        condition
    );
};

export const canViewEquipment = (credentials, equipment) => checkPermissions(credentials, equipment, readonlyRoles.concat(fullRoles));
export const canRouteEquipment = ({credentials, clientConfig}) => canViewEquipment(credentials) && clientConfig.enableEquipment

const reducer = { name: equipmentSuperSlice.name, reducer: equipmentSuperSlice.reducer }
export default reducer;